import { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Phone } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Eyebrow, FadeIn, GoldButton, OutlineButton } from "@/components/Reveal";
import { EXPERIENCES } from "@/data/experiences";
import BookingModal from "@/components/BookingModal";

export default function ExperiencePage() {
  const { slug } = useParams();
  const [bookingOpen, setBookingOpen] = useState(false);
  const exp = EXPERIENCES.find((e) => e.slug === slug);
  if (!exp) return <Navigate to="/experiences" replace />;

  const pricePerPerson = Number(
    (exp.details.find((d) => d.label === "Price").value.match(/\u20AC(\d+)/) || [])[1] || 0
  );
  const others = EXPERIENCES.filter((e) => e.slug !== slug);

  return (
    <>
      <PageHero eyebrow={`Experience ${exp.tag}`} title={exp.title} image={exp.heroImage}>
        {exp.short}
      </PageHero>

      <section className="bg-navy-800 py-24 lg:py-28">
        <div className="mx-auto grid max-w-7xl gap-16 px-5 sm:px-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <Eyebrow>The Experience</Eyebrow>
            <div className="mt-8 space-y-6">
              {exp.paragraphs.map((p, i) => (
                <FadeIn key={i} delay={i * 0.05}>
                  <p className="text-sm font-light leading-relaxed text-mist sm:text-base">{p}</p>
                </FadeIn>
              ))}
            </div>

            {(exp.included || exp.provided || exp.bring) && (
              <div className="mt-14 grid gap-10 sm:grid-cols-2">
                {(exp.included || exp.provided) && (
                  <FadeIn>
                    <div className="border border-gold/25 bg-navy-900/60 p-7">
                      <h3 className="text-[11px] font-semibold uppercase tracking-[0.26em] text-gold">
                        {exp.included ? "What's Included" : "What We Provide"}
                      </h3>
                      <ul className="mt-5 space-y-3">
                        {(exp.included || exp.provided).map((item) => (
                          <li key={item} className="flex items-start gap-3 text-sm text-ivory/90">
                            <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </FadeIn>
                )}
                {exp.bring && (
                  <FadeIn delay={0.1}>
                    <div className="border border-white/10 bg-navy-900/40 p-7">
                      <h3 className="text-[11px] font-semibold uppercase tracking-[0.26em] text-gold">
                        What to Bring
                      </h3>
                      <ul className="mt-5 space-y-3">
                        {exp.bring.map((item) => (
                          <li key={item} className="flex items-start gap-3 text-sm text-ivory/90">
                            <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" aria-hidden="true" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </FadeIn>
                )}
              </div>
            )}
          </div>

          <div>
            <FadeIn delay={0.15}>
              <div className="border border-gold/30 bg-navy-900/70 p-8 lg:sticky lg:top-28" data-testid="trip-details-card">
                <h2 className="font-serif text-3xl text-ivory">Trip Details</h2>
                <dl className="mt-7 space-y-0">
                  {exp.details.map((d) => (
                    <div
                      key={d.label}
                      className="flex items-center justify-between gap-6 border-b border-white/10 py-4 last:border-0"
                    >
                      <dt className="text-[11px] uppercase tracking-[0.2em] text-mist">{d.label}</dt>
                      <dd className={`text-right text-sm ${d.label === "Price" ? "font-semibold text-gold" : "text-ivory"}`}>
                        {d.value}
                      </dd>
                    </div>
                  ))}
                </dl>
                <GoldButton onClick={() => setBookingOpen(true)} testId={`book-tour-${exp.slug}`} className="mt-8 w-full">
                  Book This Tour
                </GoldButton>
                <OutlineButton href="/contact" testId={`tour-enquire-${exp.slug}`} className="mt-4 w-full">
                  Enquire Instead
                </OutlineButton>
                <a
                  href="tel:+353862518252"
                  data-testid={`tour-call-${exp.slug}`}
                  className="mt-4 flex items-center justify-center gap-2 border border-gold/50 py-3 text-[12px] font-semibold uppercase tracking-[0.2em] text-gold transition-colors hover:bg-gold-muted"
                >
                  <Phone className="h-3.5 w-3.5" /> +353 86 251 8252
                </a>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="bg-navy-900 py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <FadeIn>
              <img
                src={exp.bodyImage}
                alt={exp.title}
                data-testid="experience-body-image"
                className="aspect-[4/3] w-full border border-white/10 object-cover"
              />
            </FadeIn>
            <FadeIn delay={0.1}>
              {exp.secondaryImage && (
                <img
                  src={exp.secondaryImage}
                  alt={`${exp.title} on Collanmore Island`}
                  className="mb-8 aspect-[4/3] w-full border border-white/10 object-cover"
                />
              )}
              <div className="border-l-2 border-gold/60 bg-navy-800/60 p-7">
                <p className="font-serif text-2xl italic leading-snug text-ivory">
                  "Join us for a Sea Safari and experience the excitement, beauty and wonder of the
                  ocean in a whole new way."
                </p>
                <p className="mt-4 text-[11px] uppercase tracking-[0.24em] text-gold">
                  The Adventure Islands Team
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="bg-navy-950 py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Eyebrow>Other Experiences</Eyebrow>
          <div className="mt-10 grid gap-6 sm:grid-cols-3">
            {others.map((o, i) => (
              <FadeIn key={o.slug} delay={i * 0.1} className="h-full">
                <Link
                  to={`/experiences/${o.slug}`}
                  data-testid={`other-experience-${o.slug}`}
                  className="group relative block h-full min-h-[280px] overflow-hidden border border-white/5"
                >
                  <img
                    src={o.cardImage}
                    alt={o.title}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.06]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/40 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-6">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.26em] text-gold">{o.tag}</p>
                      <h3 className="mt-2 font-serif text-2xl text-ivory">{o.title}</h3>
                    </div>
                    <ArrowRight className="h-5 w-5 shrink-0 text-gold transition-transform duration-300 group-hover:translate-x-1.5" />
                  </div>
                </Link>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.2}>
            <Link
              to="/experiences"
              data-testid="back-to-experiences"
              className="mt-12 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-mist transition-colors hover:text-gold"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> All Experiences
            </Link>
          </FadeIn>
        </div>
      </section>
      <BookingModal
        open={bookingOpen}
        onClose={() => setBookingOpen(false)}
        exp={exp}
        pricePerPerson={pricePerPerson}
      />
    </>
  );
}
